/**
 * Utilitaires safe pour Android
 * Évite les crashs liés à localStorage, Date, et autres APIs
 * 
 * IMPORTANT: Ces fonctions doivent être utilisées partout dans l'app
 * pour éviter les crashs sur Android WebView
 */

import { IS_NATIVE } from './http';
import { Preferences } from '@capacitor/preferences';

/**
 * Vérifier si localStorage est disponible
 */
const isLocalStorageAvailable = () => {
  try {
    const test = '__localStorage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (e) {
    return false;
  }
};

// Cache pour éviter les vérifications répétées
let localStorageAvailable = null;

const checkLocalStorage = () => {
  if (localStorageAvailable === null) {
    localStorageAvailable = isLocalStorageAvailable();
  }
  return localStorageAvailable;
};

/**
 * Safe localStorage - utilise Preferences sur native, localStorage sur web
 * SYNCHRONOUS version for backwards compatibility (web only)
 */
export const safeLocalStorage = {
  getItem(key) {
    try {
      if (IS_NATIVE || !checkLocalStorage()) {
        // Sur native, retourner null (utiliser safeStorage async à la place)
        return null;
      }
      return localStorage.getItem(key);
    } catch (error) {
      console.warn('safeLocalStorage.getItem error:', error);
      return null;
    }
  },

  setItem(key, value) {
    try {
      if (IS_NATIVE || !checkLocalStorage()) {
        return; // Sur native, ignorer (utiliser safeStorage async)
      }
      localStorage.setItem(key, value);
    } catch (error) {
      console.warn('safeLocalStorage.setItem error:', error);
    }
  },

  removeItem(key) {
    try {
      if (IS_NATIVE || !checkLocalStorage()) {
        return;
      }
      localStorage.removeItem(key);
    } catch (error) {
      console.warn('safeLocalStorage.removeItem error:', error);
    }
  }
};

/**
 * Safe localStorage - utilise Preferences sur native, localStorage sur web
 * ASYNC version (recommended for native apps)
 */
export const safeStorage = {
  async getItem(key) {
    try {
      if (IS_NATIVE) {
        const result = await Preferences.get({ key });
        return result.value;
      }
      if (!checkLocalStorage()) {
        return null;
      }
      return localStorage.getItem(key);
    } catch (error) {
      console.warn('safeStorage.getItem error:', error);
      return null;
    }
  },

  async setItem(key, value) {
    try {
      if (IS_NATIVE) {
        await Preferences.set({ key, value: String(value) });
      } else if (checkLocalStorage()) {
        localStorage.setItem(key, value);
      }
    } catch (error) {
      console.warn('safeStorage.setItem error:', error);
    }
  },

  async removeItem(key) {
    try {
      if (IS_NATIVE) {
        await Preferences.remove({ key });
      } else if (checkLocalStorage()) {
        localStorage.removeItem(key);
      }
    } catch (error) {
      console.warn('safeStorage.removeItem error:', error);
    }
  }
};

/**
 * Mois en français pour le fallback
 */
const MOIS_FR = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 
                 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const MOIS_FR_COURT = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 
                       'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];

/**
 * Safe Date formatting - avec fallback pour Android WebView
 * @param {Date|string|number} date - La date à formater
 * @param {Object} options - Options de formatage (compatible avec toLocaleString)
 */
export const safeFormatDate = (date, options = {}) => {
  // Vérifier que la date est valide
  if (!date) return '';
  
  try {
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) {
      return '';
    }
    
    // Sur native Android, utiliser directement le fallback manuel
    // car toLocaleString peut être inconsistant
    if (IS_NATIVE) {
      return formatDateManual(d, options);
    }
    
    const defaultOptions = {
      locale: 'fr-CH',
      timeZone: 'Europe/Zurich',
      ...options
    };
    
    return d.toLocaleString(defaultOptions.locale, {
      timeZone: defaultOptions.timeZone,
      ...options
    });
  } catch (error) {
    console.warn('safeFormatDate error, using fallback:', error);
    return formatDateManual(date, options);
  }
};

/**
 * Formatage manuel de date (fallback robuste)
 */
const formatDateManual = (date, options = {}) => {
  try {
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) {
      return '';
    }
    
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    
    // Si options demande un format avec mois en texte
    if (options.month === 'long') {
      return `${d.getDate()} ${MOIS_FR[d.getMonth()]} ${year}`;
    }
    if (options.month === 'short') {
      return `${d.getDate()} ${MOIS_FR_COURT[d.getMonth()]} ${year}`;
    }
    
    // Format par défaut
    if (options.hour !== undefined || options.minute !== undefined) {
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    }
    
    return `${day}/${month}/${year}`;
  } catch (e) {
    return '';
  }
};

/**
 * Safe Date formatting (date only)
 * @param {Date|string|number} date - La date à formater
 * @param {Object} options - Options de formatage
 */
export const safeFormatDateOnly = (date, options = {}) => {
  if (!date) return '';
  
  try {
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) {
      return '';
    }
    
    // Sur native Android, utiliser directement le fallback manuel
    if (IS_NATIVE) {
      return formatDateManual(d, options);
    }
    
    return d.toLocaleDateString('fr-CH', {
      timeZone: 'Europe/Zurich',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      ...options
    });
  } catch (error) {
    console.warn('safeFormatDateOnly error, using fallback:', error);
    return formatDateManual(date, options);
  }
};

/**
 * Safe Time formatting (time only)
 * @param {Date|string|number} date - La date à formater
 */
export const safeFormatTime = (date) => {
  if (!date) return '';
  
  try {
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) {
      return '';
    }
    
    if (IS_NATIVE) {
      const hours = String(d.getHours()).padStart(2, '0');
      const minutes = String(d.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    }
    
    return d.toLocaleTimeString('fr-CH', {
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    try {
      const d = date instanceof Date ? date : new Date(date);
      const hours = String(d.getHours()).padStart(2, '0');
      const minutes = String(d.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    } catch (e) {
      return '';
    }
  }
};

/**
 * Temps relatif lisible : "à l'instant", "il y a 5 min", "il y a 2 h",
 * "hier à 14:30", "il y a 3 j", puis date complète. Fuseau Europe/Zurich géré via safeFormatTime.
 */
export const formatRelativeTime = (date) => {
  if (!date) return '';
  try {
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return '';
    const now = new Date();
    const diffSec = Math.floor((now - d) / 1000);

    if (diffSec < 0) return safeFormatTime(d);           // futur -> juste l'heure
    if (diffSec < 60) return "à l'instant";
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `il y a ${diffMin} min`;
    const diffH = Math.floor(diffMin / 60);

    const isSameDay = d.getDate() === now.getDate() && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    if (isSameDay) return `il y a ${diffH} h`;

    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    const isYesterday = d.getDate() === yesterday.getDate() && d.getMonth() === yesterday.getMonth() && d.getFullYear() === yesterday.getFullYear();
    if (isYesterday) return `hier à ${safeFormatTime(d)}`;

    const diffDays = Math.floor(diffH / 24);
    if (diffDays < 7) return `il y a ${diffDays} j`;
    return `le ${safeFormatDateOnly(d)}`;
  } catch (error) {
    return '';
  }
};


/**
 * Safe array operations - évite les crashs sur null/undefined
 */
export const safeArray = (arr) => {
  if (!arr) return [];
  if (!Array.isArray(arr)) return [];
  return arr;
};

/**
 * Safe object access
 */
export const safeGet = (obj, path, defaultValue = null) => {
  try {
    const keys = path.split('.');
    let result = obj;
    for (const key of keys) {
      if (result === null || result === undefined) {
        return defaultValue;
      }
      result = result[key];
    }
    return result ?? defaultValue;
  } catch (error) {
    return defaultValue;
  }
};

/**
 * Safe JSON parse
 */
export const safeJsonParse = (str, defaultValue = null) => {
  try {
    if (!str) return defaultValue;
    return JSON.parse(str);
  } catch (error) {
    console.warn('safeJsonParse error:', error);
    return defaultValue;
  }
};

/**
 * Safe async operation wrapper
 */
export const safeAsync = async (fn, fallback = null) => {
  try {
    return await fn();
  } catch (error) {
    console.warn('safeAsync error:', error);
    return fallback;
  }
};

/**
 * Debounce function pour éviter les appels répétés
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Throttle function pour limiter les appels
 */
export const throttle = (func, limit) => {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

/**
 * Safe number formatting with toLocaleString
 * @param {number} num - Le nombre à formater
 */
export const safeFormatNumber = (num) => {
  if (num === null || num === undefined) return '0';
  
  try {
    if (IS_NATIVE) {
      // Formatage manuel avec séparateurs de milliers
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    }
    return num.toLocaleString('fr-CH');
  } catch (error) {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  }
};

export default {
  safeStorage,
  safeLocalStorage,
  safeFormatDate,
  safeFormatDateOnly,
  safeFormatTime,
  safeFormatNumber,
  safeArray,
  safeGet,
  safeJsonParse,
  safeAsync,
  debounce,
  throttle
};
